import type React from "react";
import { Label } from "@heroui/react";
import { CheckboxButtonGroup } from "@heroui-pro/react/checkbox-button-group";
import { m, useReducedMotion } from "framer-motion";
import { memo, useEffect, useRef, useState } from "react";
import { ChoiceCard, type ChoiceCardBadge } from "@funnel-ui/primitives/ChoiceCard";
import { DynamicIcon } from "@funnel-ui/primitives/DynamicIcon";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

export type MultiChoiceOption = {
  id: string;
  label: string;
  description?: string;
  imageUrl?: string;
  /** CSS `object-position` for cover images (e.g. "top", "center"). */
  imagePosition?: string;
  imageAspect?: "square" | "portrait" | "landscape";
  iconLeading?: string;
  badge?: ChoiceCardBadge;
  disabled?: boolean;
};

type MultiChoiceProps = {
  question: string;
  description?: string;
  /** Small uppercase label rendered above the question in accent color. */
  eyebrow?: string;
  options: MultiChoiceOption[];
  initialValue?: string[];
  /** Minimum required selections. Aliased by `min` for back-compat. */
  minSelections?: number;
  /** Maximum allowed selections. Aliased by `max` for back-compat. */
  maxSelections?: number;
  /** @deprecated use `minSelections` */
  min?: number;
  /** @deprecated use `maxSelections` */
  max?: number;
  /**
   * Override the auto-generated hint text under the question. When provided,
   * replaces the dynamic "Pick at least N more" / "N of M selected" copy.
   */
  helperText?: string;
  /**
   * When true, the helper text shakes once if the user attempts to select
   * beyond the configured max. Honors `prefers-reduced-motion`.
   */
  shakeOnMaxReached?: boolean;
  /**
   * When true and maxSelections is reached, selecting a new option
   * automatically deselects the oldest selection (FIFO queue).
   */
  swapOnMax?: boolean;
  /** Fired when the user attempts to exceed the max. Useful for analytics. */
  onMaxReached?: () => void;
  layout?: "row" | "tile" | "chip";
  columns?: 1 | 2 | 3;
  /** When false, hides the checkbox indicator on row layout. Defaults to true. */
  showIndicator?: boolean;
  onChange?: (optionIds: string[]) => void;
  onValidityChange?: (valid: boolean) => void;
  className?: string;
  testId?: string;
};

const buildHint = (selectedCount: number, min: number, max: number): string => {
  if (selectedCount < min) {
    const remaining = min - selectedCount;
    return `Pick at least ${remaining} more`;
  }
  if (selectedCount === max) {
    return `Maximum ${max} reached`;
  }
  return `${selectedCount} of ${max} selected`;
};

const resolveGridCols = (columns: 1 | 2 | 3): string => {
  if (columns === 3) return "grid grid-cols-3 gap-3";
  if (columns === 2) return "grid grid-cols-2 gap-3";
  return "flex flex-col gap-3";
};

const ShakeHint = ({
  text,
  shakeKey,
  testId,
}: {
  text: string;
  shakeKey: number;
  testId?: string;
}) => (
  <m.span
    key={shakeKey}
    animate={shakeKey > 0 ? { x: [0, -4, 4, -3, 3, 0] } : { x: 0 }}
    transition={{ duration: 0.32, ease: "easeOut" }}
    data-testid={testId}
    className="inline-block"
  >
    {text}
  </m.span>
);

type SelectionGuardArgs = {
  next: string[];
  max: number;
  shakeEnabled: boolean;
  prevAtMax: boolean;
  onShake: () => void;
  onMaxReached: (() => void) | undefined;
};

const renderOptionCard = (
  option: MultiChoiceOption,
  index: number,
  selected: string[],
  atMax: boolean,
  layout: "row" | "tile" | undefined,
  columns: 1 | 2 | 3,
  swapOnMax?: boolean,
  showIndicator?: boolean,
): React.ReactNode => {
  const isSelected = selected.includes(option.id);
  const blockBecauseAtMax = !swapOnMax && atMax && !isSelected;
  return (
    <ChoiceCard
      key={option.id}
      role="checkbox"
      value={option.id}
      label={option.label}
      description={option.description}
      imageUrl={option.imageUrl}
      imagePosition={option.imagePosition}
      imageAspect={option.imageAspect}
      iconLeading={option.iconLeading && <DynamicIcon icon={option.iconLeading} size={20} />}
      badge={option.badge}
      layout={layout ?? (columns >= 2 ? "tile" : "row")}
      disabled={option.disabled || blockBecauseAtMax}
      showIndicator={showIndicator}
      optionIndex={index}
    />
  );
};

const resolveBounds = ({
  minSelections,
  maxSelections,
  min,
  max,
  optionCount,
}: {
  minSelections?: number;
  maxSelections?: number;
  min?: number;
  max?: number;
  optionCount: number;
}): { resolvedMin: number; effectiveMax: number } => ({
  resolvedMin: minSelections ?? min ?? 1,
  effectiveMax: maxSelections ?? max ?? optionCount,
});

type SelectionGuardResult = { allowed: boolean; nowAtMax: boolean };

const guardSelection = ({
  next,
  max,
  shakeEnabled,
  prevAtMax,
  onShake,
  onMaxReached,
}: SelectionGuardArgs): SelectionGuardResult => {
  if (next.length > max) {
    if (shakeEnabled) onShake();
    onMaxReached?.();
    return { allowed: false, nowAtMax: prevAtMax };
  }
  const nowAtMax = next.length === max;
  if (shakeEnabled && nowAtMax && !prevAtMax) onShake();
  return { allowed: true, nowAtMax };
};

/**
 * Inline style overrides for chip layout. HeroUI Pro's
 * `.checkbox-button-group__item` CSS is unlayered and beats Tailwind's
 * `@layer utilities`, so `!important` class utilities alone aren't enough.
 * Inline styles have the highest cascade specificity and reliably override.
 */
const CHIP_STYLE_BASE: React.CSSProperties = {
  display: "inline-flex",
  width: "auto",
  flex: "none",
  flexDirection: "row",
  alignItems: "center",
  gap: "0.5rem",
  borderRadius: "9999px",
  borderWidth: "1px",
  borderStyle: "solid",
  paddingInline: "1rem",
  paddingBlock: "0.625rem",
  cursor: "pointer",
};

const CHIP_STYLE_SELECTED: React.CSSProperties = {
  ...CHIP_STYLE_BASE,
  borderColor: "hsl(var(--brand-primary))",
  backgroundColor: "hsl(var(--brand-primary) / 0.15)",
};

const CHIP_STYLE_UNSELECTED: React.CSSProperties = {
  ...CHIP_STYLE_BASE,
  borderColor: "hsl(var(--brand-bg-foreground) / 0.15)",
  backgroundColor: "transparent",
};

const CHIP_STYLE_HOVER: React.CSSProperties = {
  ...CHIP_STYLE_BASE,
  borderColor: "hsl(var(--brand-primary) / 0.5)",
  backgroundColor: "transparent",
};

const ChipOption = memo(
  ({
    option,
    isSelected,
    isDisabledByMax,
    testId,
  }: {
    option: MultiChoiceOption;
    isSelected: boolean;
    isDisabledByMax?: boolean;
    testId?: string;
  }) => {
    const [hovered, setHovered] = useState(false);

    const style = isSelected
      ? CHIP_STYLE_SELECTED
      : hovered
        ? CHIP_STYLE_HOVER
        : CHIP_STYLE_UNSELECTED;

    return (
      <CheckboxButtonGroup.Item
        value={option.id}
        isDisabled={option.disabled || isDisabledByMax}
        data-testid={testId}
        className="transition-colors duration-150"
        style={style}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        <CheckboxButtonGroup.ItemContent
          className="flex-row items-center gap-2 p-0"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "0.5rem",
            padding: 0,
          }}
        >
          {option.iconLeading && (
            <span aria-hidden="true" className="text-lg">
              <DynamicIcon icon={option.iconLeading} size={18} />
            </span>
          )}
          <Label>{option.label}</Label>
        </CheckboxButtonGroup.ItemContent>
      </CheckboxButtonGroup.Item>
    );
  },
);
ChipOption.displayName = "ChipOption";

const MultiChoiceImpl = ({
  question,
  description,
  eyebrow,
  options,
  initialValue = [],
  min,
  max,
  minSelections,
  maxSelections,
  helperText,
  shakeOnMaxReached = false,
  swapOnMax = false,
  onMaxReached,
  layout,
  columns = 1,
  showIndicator = true,
  onChange,
  onValidityChange,
  className,
  testId,
}: MultiChoiceProps) => {
  const { resolvedMin, effectiveMax } = resolveBounds({
    minSelections,
    maxSelections,
    min,
    max,
    optionCount: options.length,
  });
  const [selected, setSelected] = useState<string[]>(initialValue);
  const [shakeNonce, setShakeNonce] = useState(0);
  const prefersReduced = useReducedMotion();
  const prevAtMaxRef = useRef(false);

  const shakeEnabled = shakeOnMaxReached && !prefersReduced;
  const isValid = selected.length >= resolvedMin && selected.length <= effectiveMax;
  const atMax = selected.length === effectiveMax;

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleChange = (next: string[]): void => {
    if (swapOnMax && next.length > effectiveMax) {
      const added = next.find((id) => !selected.includes(id));
      if (added) {
        const swapped = [...selected.slice(1), added];
        prevAtMaxRef.current = true;
        setSelected(swapped);
        onChange?.(swapped);
        return;
      }
    }
    const guard = guardSelection({
      next,
      max: effectiveMax,
      shakeEnabled,
      prevAtMax: prevAtMaxRef.current,
      onShake: () => setShakeNonce((n) => n + 1),
      onMaxReached,
    });
    if (!guard.allowed) return;
    prevAtMaxRef.current = guard.nowAtMax;
    setSelected(next);
    onChange?.(next);
  };

  const hintText = helperText ?? buildHint(selected.length, resolvedMin, effectiveMax);
  const hintNode = shakeEnabled ? (
    <ShakeHint
      text={hintText}
      shakeKey={shakeNonce}
      testId={testId ? `${testId}-hint` : undefined}
    />
  ) : (
    hintText
  );

  const isChip = layout === "chip";

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading
        title={question}
        description={description}
        eyebrow={eyebrow}
        hint={isChip ? undefined : hintNode}
        editablePath="question"
      />
      <CheckboxButtonGroup
        aria-label={question}
        className={isChip ? "flex flex-wrap gap-2.5" : resolveGridCols(columns)}
        style={
          isChip
            ? { display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "0.625rem" }
            : undefined
        }
        value={selected}
        onChange={handleChange}
        name={`multi-choice-${testId ?? "default"}`}
        variant="secondary"
        layout={isChip ? "flex" : columns >= 2 ? "grid" : "flex"}
      >
        {isChip
          ? options.map((option) => (
              <ChipOption
                key={option.id}
                option={option}
                isSelected={selected.includes(option.id)}
                isDisabledByMax={!swapOnMax && atMax && !selected.includes(option.id)}
              />
            ))
          : options.map((option, index) =>
              renderOptionCard(
                option,
                index,
                selected,
                atMax,
                layout,
                columns,
                swapOnMax,
                showIndicator,
              ),
            )}
      </CheckboxButtonGroup>
      {isChip && (
        <p className="m-0 text-xs" style={{ color: "hsl(var(--hero-fg-muted))" }}>
          {hintNode}
        </p>
      )}
      <p role="status" aria-live="polite" className="sr-only">
        {selected.length} of {effectiveMax} selected
      </p>
    </section>
  );
};

export const MultiChoice = memo(MultiChoiceImpl);

export default MultiChoice;
